// Who is signed in, and what they may open. Permission changes take effect on the next page load,
// so this is fetched once per session and refreshed after sign-in or a permission change.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { forgetSignedIn } from '@/offline/identity.ts';
import { api, errorText } from './client.ts';
import type { Level, Me, Section } from './types.ts';

export const ME_KEY = ['me'] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => api.get<Me>('/auth/me'),
    retry: false,
    staleTime: 60_000,
  });
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/logout'),
    onSuccess: async () => {
      // On a shared phone the next person must not be shown what this one opened.
      await forgetSignedIn();
      client.clear();
      window.location.href = '/auth';
    },
    // Offline, the server cannot end the session, so nothing here would really be signed out.
    onError: (err) => toast.error(err instanceof TypeError ? 'Signing out needs a connection.' : errorText(err)),
  });
}

const RANK: Record<Level, number> = { none: 0, view: 1, edit: 2 };

export function access(me: Me | undefined) {
  const level = (section: Section): Level => me?.permissions?.[section] ?? 'none';
  return {
    me,
    level,
    canView: (section: Section) => RANK[level(section)] >= RANK.view,
    canEdit: (section: Section) => RANK[level(section)] >= RANK.edit,
    isOwner: !!me?.isOwner,
    isFullAdmin: !!me?.isFullAdmin,
  };
}

export type Access = ReturnType<typeof access>;
